'use client';
import { analytics } from '@lib/analytics/page-events';
import { signIn } from '@lib/auth';
import { useMicrocopy } from '@lib/cms/microcopy/client';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import type { ProductMembership } from '@lib/types/api/ProductListV2';
import { useSession } from 'next-auth/react';
import { PropsWithChildren, useCallback, useMemo } from 'react';
import { useSWRConfig } from 'swr';
import {
  FavoriteListModal,
  SaveToListModal,
  SnackBar,
  type FavoriteListModalFormData,
  type ListData,
} from 'ui-components';

import { ProductListsContext } from './ProductListsContext';
import { addItemToList, createList } from './productListsApi';
import {
  buildSaveToastContent,
  getListChanges,
  syncListChanges,
  toListData,
  trackListChangesAnalytics,
  type ProductInfo,
} from './productListsHelpers';
import {
  PRODUCT_LISTS_MICROCOPY_DEFAULTS,
  buildChangeModalMicrocopy,
  buildCreateModalMicrocopy,
} from './productListsMicrocopy';
import { useListsData } from './useListsData';
import { useProductMembership } from './useProductMembership';
import { useSaveToListModal } from './useSaveToListModal';
import { useToast } from './useToast';

export const ProductListsProvider = ({ children }: PropsWithChildren) => {
  const { flags } = useFlags();
  const isProductListsEnabled =
    flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;
  const { status, data: session } = useSession();
  const isAuthenticated = status === 'authenticated';
  const isUnauthenticated = status === 'unauthenticated';
  const shouldFetch = isAuthenticated && isProductListsEnabled;

  const mc = useMicrocopy('product-lists', PRODUCT_LISTS_MICROCOPY_DEFAULTS);

  const {
    personalLists,
    orgLists,
    orgListIds,
    activeOrganization,
    organizationId,
    isLoading,
    mutatePersonalLists,
    mutateOrgLists,
  } = useListsData(shouldFetch);

  const {
    isProductInList: getProductInList,
    getMembership,
    fetchProductMembership,
    fetchBatchProductMembership,
    setMembership,
  } = useProductMembership(shouldFetch, organizationId);

  const isProductInList = useCallback(
    (productId: string): boolean | null =>
      isProductListsEnabled && isUnauthenticated
        ? false
        : getProductInList(productId),
    [isProductListsEnabled, isUnauthenticated, getProductInList]
  );

  const modal = useSaveToListModal();
  const { openSave } = modal;
  const { toast, showToast, dismissToast } = useToast();

  const { mutate: globalMutate } = useSWRConfig();

  const invalidateListItems = useCallback(
    (listIds: string[]) => {
      if (listIds.length === 0) return;
      globalMutate(
        (key) =>
          typeof key === 'string' &&
          listIds.some(
            (id) =>
              key.startsWith(`/api/lists/${id}/items`) ||
              (organizationId &&
                key.startsWith(
                  `/api/organizations/${organizationId}/lists/${id}/items`
                ))
          )
      );
    },
    [globalMutate, organizationId]
  );

  const getOrgIdForList = useCallback(
    (listId: string) => (orgListIds.has(listId) ? organizationId : undefined),
    [orgListIds, organizationId]
  );

  const listNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const list of personalLists) map.set(list.id, list.name);
    for (const list of orgLists) map.set(list.id, list.name);
    return map;
  }, [personalLists, orgLists]);

  const toastMicrocopy = useMemo(
    () => ({
      savedTo: mc['product-lists.saved-to'],
      removedFrom: mc['product-lists.removed-from'],
      multipleLists: mc['product-lists.multiple-lists'],
    }),
    [mc]
  );

  const toggleProductList = useCallback(
    (product: ProductInfo) => {
      if (!isAuthenticated) {
        signIn();
        return;
      }
      const membership = getMembership(product.id);
      openSave(product, [...membership.personal, ...membership.organization]);
    },
    [isAuthenticated, getMembership, openSave]
  );

  const handleSave = useCallback(
    async (selectedLists: ListData[]) => {
      const { product, initialSelectedListIds, fromCreateFlow } = modal;
      if (!product) return;

      const { listsToAdd, idsToRemove } = getListChanges(
        initialSelectedListIds,
        selectedLists
      );

      // Nothing to persist (e.g. confirming an inline create) — just toast.
      if (listsToAdd.length === 0 && idsToRemove.length === 0) {
        if (fromCreateFlow) {
          if (modal.createdListName) {
            showToast(mc['product-lists.saved-to'], modal.createdListName);
          } else {
            showToast(mc['product-lists.saved']);
          }
        }
        modal.close();
        return;
      }

      modal.setSubmitting(true);
      modal.setServerError(false);
      try {
        await syncListChanges({
          listsToAdd,
          idsToRemove,
          productId: product.id,
          getOrgIdForList,
        });
        trackListChangesAnalytics({
          listsToAdd,
          idsToRemove,
          productId: product.id,
          productName: product.name,
        });

        const newMembership: ProductMembership = {
          personal: selectedLists.filter((l) => l.personal).map((l) => l.id),
          organization: selectedLists
            .filter((l) => !l.personal)
            .map((l) => l.id),
        };
        setMembership(product.id, newMembership);

        const changedIds = [...listsToAdd.map((l) => l.id), ...idsToRemove];
        await mutatePersonalLists();
        if (changedIds.some((id) => orgListIds.has(id))) await mutateOrgLists();
        invalidateListItems(changedIds);

        const toastContent = buildSaveToastContent({
          addedNames: listsToAdd.map((l) => l.name),
          removedNames: idsToRemove.map((id) => listNameById.get(id) ?? id),
          microcopy: toastMicrocopy,
        });
        if (toastContent) showToast(toastContent.title, toastContent.listLabel);
        else showToast(mc['product-lists.saved']);
        modal.close();
      } catch (error) {
        console.error('Error syncing list changes:', error);
        modal.setSubmitting(false);
        modal.setServerError(true);
      }
    },
    [
      modal,
      getOrgIdForList,
      setMembership,
      mutatePersonalLists,
      mutateOrgLists,
      orgListIds,
      invalidateListItems,
      listNameById,
      toastMicrocopy,
      showToast,
      mc,
    ]
  );

  const createListAndAddProduct = useCallback(
    async (formData: FavoriteListModalFormData) => {
      const { product } = modal;
      if (!product) return;

      const isOrgList = formData.accountType === 'organization';
      const targetOrgId = isOrgList ? organizationId : undefined;

      modal.setSubmitting(true);
      modal.setServerError(false);
      try {
        const newList = await createList(
          formData.listName,
          formData.visibility,
          targetOrgId
        );
        analytics.pushCreateListEvent({
          list_id: newList.id,
          list_name: formData.listName,
          list_visibility: formData.visibility,
          list_type: isOrgList ? 'organization' : 'personal',
          product_id: product.id,
          product_name: product.name,
          organization_id: targetOrgId,
        });

        await addItemToList(newList.id, product.id, targetOrgId);
        analytics.pushSaveToListEvent({
          asset_id: product.id,
          asset_name: product.name,
        });

        if (isOrgList) await mutateOrgLists();
        else await mutatePersonalLists();
        invalidateListItems([newList.id]);

        const prev = getMembership(product.id);
        const newMembership: ProductMembership = isOrgList
          ? {
              personal: prev.personal,
              organization: [...prev.organization, newList.id],
            }
          : {
              personal: [...prev.personal, newList.id],
              organization: prev.organization,
            };
        setMembership(product.id, newMembership);

        modal.returnToSaveAfterCreate(
          [...newMembership.personal, ...newMembership.organization],
          newList.name
        );
      } catch (error) {
        console.error('Error creating list and adding product:', error);
        modal.setSubmitting(false);
        modal.setServerError(true);
      }
    },
    [
      modal,
      organizationId,
      mutateOrgLists,
      mutatePersonalLists,
      invalidateListItems,
      getMembership,
      setMembership,
    ]
  );

  const createModalMicrocopy = useMemo(
    () => buildCreateModalMicrocopy(mc),
    [mc]
  );
  const changeModalMicrocopy = useMemo(
    () => buildChangeModalMicrocopy(mc, modal.fromCreateFlow),
    [mc, modal.fromCreateFlow]
  );

  const value = useMemo(
    () => ({
      personalLists,
      orgLists,
      isLoading,
      isProductInList,
      toggleProductList,
      fetchProductMembership,
      fetchBatchProductMembership,
    }),
    [
      personalLists,
      orgLists,
      isLoading,
      isProductInList,
      toggleProductList,
      fetchProductMembership,
      fetchBatchProductMembership,
    ]
  );

  return (
    <ProductListsContext.Provider value={value}>
      {children}

      {toast && (
        <SnackBar
          key={toast.key}
          icon="BookmarkSimple"
          iconWeight="fill"
          closeIcon="X"
          onClose={dismissToast}
          microcopy={{
            title: toast.title,
            ...(toast.listLabel ? { actionLabel: toast.listLabel } : {}),
            closeAriaLabel: mc['product-lists.close'],
          }}
        />
      )}

      <FavoriteListModal
        open={modal.view === 'create'}
        onOpenChange={(open) => {
          if (!open) modal.close();
        }}
        listName=""
        visibility="private"
        accountType={organizationId ? modal.createAccountType : 'personal'}
        showAccountType={!!organizationId}
        userName={session?.user?.name ?? ''}
        userProfilePicture={session?.user?.image}
        publisherName={activeOrganization?.name ?? ''}
        microcopy={createModalMicrocopy}
        onSave={createListAndAddProduct}
        isSubmitting={modal.isSubmitting}
        position="bottom"
        showBackdrop={false}
        serverError={modal.serverError}
        onDismissError={() => modal.setServerError(false)}
      />

      {modal.view === 'save' && (
        <SaveToListModal
          key={modal.openToken}
          open
          onOpenChange={(open) => {
            if (!open) modal.close();
          }}
          list={[
            ...personalLists.map((l) => toListData(l, true)),
            ...orgLists.map((l) => toListData(l, false)),
          ]}
          multiTabs={!!organizationId}
          initialSelectedListIds={modal.initialSelectedListIds}
          forceEnableSave={modal.fromCreateFlow}
          userName={session?.user?.name ?? ''}
          userProfilePicture={session?.user?.image}
          publisherName={activeOrganization?.name ?? ''}
          microcopy={changeModalMicrocopy}
          onNewList={modal.openCreate}
          onSave={handleSave}
          isSubmitting={modal.isSubmitting}
          position="bottom"
          showBackdrop={false}
          serverError={modal.serverError}
          onDismissError={() => modal.setServerError(false)}
        />
      )}
    </ProductListsContext.Provider>
  );
};
